import { IconButton } from "@components/button"
import { Input } from "@components/input"
import { useHotkey } from "@hooks/use-hotkey"
import { BlockIcon } from "@icons/block-icon"
import { SaveIcon } from "@icons/save-icon"
import { Popper } from "@mui/material"
import clsx from "clsx"
import type { InputHTMLAttributes } from "react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { leftClickEvent } from "../../../utils/event"

export type ValueType = InputHTMLAttributes<HTMLInputElement>["value"]

export type ReorderButtonGroupProps = {
	disabled?: boolean
	className?: string
	inputMaxValue: number
	defaultInputValue: number
	onReorder: (rank: number) => void
}

export function ReorderButtonGroup({
	disabled = false,
	inputMaxValue,
	defaultInputValue,
	className,
	onReorder,
}: ReorderButtonGroupProps) {
	const inputRef = useRef<HTMLInputElement>(null)
	const [value, setValue] = useState<number>(defaultInputValue)
	const [isDirty, setIsDirty] = useState<boolean>(false)

	const inputProps = useMemo(
		() => ({
			min: 1,
			max: inputMaxValue,
		}),
		[inputMaxValue],
	)

	useEffect(() => {
		setValue(defaultInputValue)
	}, [defaultInputValue])

	const onSubmit = useCallback(() => {
		setIsDirty(false)
		if (typeof value === "number") onReorder(value)
	}, [value, onReorder])
	useHotkey("Escape", () => {
		setIsDirty(false)
	})

	return (
		<div
			role="none"
			onFocus={() => {
				inputRef?.current?.focus()
			}}
			className={clsx("flex gap-2", className)}
		>
			<Input
				ref={inputRef}
				{...inputProps}
				disabled={disabled}
				className="w-10 text-center"
				type="number"
				value={value}
				onChange={(e) => {
					const parsed = Number.parseInt(e.target.value, 10)
					if (Number.isNaN(parsed)) setValue(1)
					else {
						if (parsed < inputProps.min || parsed > inputProps.max) return
						setValue(parsed)
					}
					setIsDirty(parsed !== defaultInputValue)
				}}
				onKeyDown={(e) => {
					if (e.key === "Enter") onSubmit()
				}}
			/>
			{
				<Popper open={isDirty} placement="right" sx={{ zIndex: 1 }} anchorEl={inputRef?.current}>
					<div className="p-2 bg-white drop-shadow-xl flex gap-2">
						<IconButton
							disabled={disabled}
							color="danger"
							title="Cancel"
							type="button"
							onMouseDown={leftClickEvent(() => {
								setValue(defaultInputValue)
								setIsDirty(false)
							})}
						>
							<BlockIcon />
						</IconButton>
						<IconButton disabled={disabled} color="success" title="Confirm" type="button" onMouseDown={onSubmit}>
							<SaveIcon />
						</IconButton>
					</div>
				</Popper>
			}
		</div>
	)
}
